import React, { useEffect, useMemo, useState } from 'react';
import { api, uploadFile } from '../api';
import MaterialPicker from '../components/MaterialPicker';

const UNIT_MAP = {
  pcs: 'pcs',
  kg: 'kg',
  g: 'g',
  ml: 'ml',
  l: 'l',
  ltr: 'l',
};

const today = new Date().toISOString().slice(0, 10);

const emptyItem = {
  raw_material_id: '',
  quantity: '',
  rate: '',
  tax_percent: '',
};

export default function PurchasesPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [purchases, setPurchases] = useState([]);

  const [newSupplier, setNewSupplier] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [invoiceNo, setInvoiceNo] = useState('');

  const [items, setItems] = useState([
    { ...emptyItem },
  ]);

  const [isPaid, setIsPaid] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');

  const [parsing, setParsing] = useState(false);
  const [parsedNotice, setParsedNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const [search, setSearch] = useState('');

  const loadSuppliers = () =>
    api
      .get('/purchases/suppliers')
      .then(setSuppliers)
      .catch(() => setSuppliers([]));

  const loadMaterials = () =>
    api
      .get('/raw-materials')
      .then(setMaterials)
      .catch(() => setMaterials([]));

  const loadPurchases = () =>
    api
      .get('/purchases')
      .then(setPurchases)
      .catch(() => setPurchases([]));

  useEffect(() => {
    loadSuppliers();
    loadMaterials();
    loadPurchases();
  }, []);

  /* =====================================================
     SUPPLIER
     ===================================================== */

  const addSupplier = async () => {
    if (!newSupplier.trim()) {
      alert('Enter supplier name');
      return;
    }

    try {
      const s = await api.post(
        '/purchases/suppliers',
        {
          name: newSupplier.trim(),
        }
      );

      setNewSupplier('');
      await loadSuppliers();
      setSupplierId(String(s.id));
    } catch (err) {
      alert(err.message);
    }
  };

  /* =====================================================
     ITEMS
     ===================================================== */

  const updateItem = (
    index,
    field,
    value
  ) => {
    setItems((list) =>
      list.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  };

  const addItemLine = () => {
    setItems((list) => [
      ...list,
      { ...emptyItem },
    ]);
  };

  const removeItemLine = (index) => {
    setItems((list) => {
      if (list.length === 1) {
        return [{ ...emptyItem }];
      }

      return list.filter(
        (_, i) => i !== index
      );
    });
  };

  const materialUnit = (id) =>
    materials.find(
      (m) => String(m.id) === String(id)
    )?.unit || '';

  /* =====================================================
     CALCULATIONS
     ===================================================== */

  const lineBase = (item) =>
    (Number(item.quantity) || 0) *
    (Number(item.rate) || 0);

  const lineTax = (item) =>
    lineBase(item) *
    ((Number(item.tax_percent) || 0) /
      100);

  const lineAmount = (item) =>
    lineBase(item) + lineTax(item);

  const subTotal = items.reduce(
    (sum, item) =>
      sum + lineBase(item),
    0
  );

  const totalTax = items.reduce(
    (sum, item) =>
      sum + lineTax(item),
    0
  );

  const grandTotal =
    subTotal + totalTax;

  /* =====================================================
     FORM RESET
     ===================================================== */

  const resetForm = () => {
    setItems([{ ...emptyItem }]);
    setInvoiceNo('');
    setInvoiceDate(today);
    setSupplierId('');
    setNewSupplier('');
    setIsPaid(false);
    setPaymentMode('cash');
    setParsedNotice(null);
    setEditing(null);
  };

  /* =====================================================
     SAVE PURCHASE
     ===================================================== */

  const setPackSize = async (id) => {
    const m = materials.find((x) => String(x.id) === String(id));
    if (!m) return;
    const current = Number(m.pack_size) > 1 ? Number(m.pack_size) : 1;
    const v = window.prompt(
      `${m.name}: how many ${m.unit} are in ONE pack / box / packet?\n\nExample: 2500 for a 2.5 kg packet (if the unit is g).\nEnter 1 if you buy it loose (quantity entered in ${m.unit}).`,
      String(current)
    );
    if (v === null) return;
    const size = Number(v);
    if (!(size >= 1)) { alert('Enter a number of 1 or more'); return; }
    try {
      await api.post(`/purchases/materials/${m.id}/pack`, { pack_size: size });
      await loadMaterials();
      if (size !== current) {
        alert(size > 1
          ? `Saved: 1 pack of ${m.name} = ${size} ${m.unit}.\nEnter Qty as number of packs and Rate as the price per pack.`
          : `Saved: ${m.name} is now entered loose, in ${m.unit}.`);
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const packOf = (id) => {
    const m = materials.find((x) => String(x.id) === String(id));
    return m && Number(m.pack_size) > 1 ? Number(m.pack_size) : 1;
  };

  const startEdit = async (purchase) => {
    try {
      const full = await api.get(`/purchases/${purchase.id}`);
      const total = Number(full.total_amount) || 0;
      const paid = Number(full.paid_amount) || 0;
      setEditing({ id: full.id, invoice_no: full.invoice_no, paid_amount: paid, total_amount: total });
      setSupplierId(String(full.supplier_id));
      setInvoiceNo(full.invoice_no || '');
      setInvoiceDate(full.purchase_date || today);
      setIsPaid(total > 0 && paid >= total - 0.01);
      setPaymentMode(full.payment_mode && full.payment_mode !== 'pending' ? full.payment_mode : 'cash');
      setParsedNotice(null);
      setItems(full.items.map((it) => {
        const p = packOf(it.raw_material_id);
        return {
          id: it.id,
          raw_material_id: String(it.raw_material_id),
          quantity: String(Math.round((it.quantity / p) * 1000) / 1000),
          rate: String(Math.round(it.rate * p * 10000) / 10000),
          tax_percent: it.tax_percent ? String(it.tax_percent) : '',
        };
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert(e.message);
    }
  };

  const savePurchase = async () => {
    if (!supplierId) {
      alert('Choose or add a supplier');
      return;
    }

    const unmapped = items.filter(
      (item) => !item.raw_material_id && (item.quantity || item.rate || item.invoice_name)
    );
    if (unmapped.length > 0) {
      alert(`${unmapped.length} line(s) have no raw material selected. Pick one from the dropdown for every red row.`);
      return;
    }

    const aliasItems = items
      .filter((i) => i.invoice_name && i.raw_material_id)
      .map((i) => ({ invoice_name: i.invoice_name, raw_material_id: Number(i.raw_material_id) }));
    if (aliasItems.length > 0) {
      api.post('/purchases/item-aliases', { supplier_id: Number(supplierId), items: aliasItems }).catch(() => {});
    }

    const validItems = items
      .filter(
        (item) =>
          item.raw_material_id &&
          item.quantity &&
          item.rate
      )
      .map((item) => ({
        id: item.id || undefined,
        raw_material_id:
          Number(item.raw_material_id),
        quantity:
          Number(item.quantity) * packOf(item.raw_material_id),
        rate:
          Number(item.rate) / packOf(item.raw_material_id),
        tax_percent:
          Number(item.tax_percent) || 0,
      }));

    if (validItems.length === 0) {
      alert(
        'Add at least one purchase item'
      );
      return;
    }

    setSaving(true);

    try {
      const keepPartial = editing && editing.paid_amount > 0 && editing.paid_amount < editing.total_amount - 0.01;
      const payload = {
        supplier_id: Number(supplierId),
        invoice_no: invoiceNo || null,
        purchase_date: invoiceDate,
        paid_amount: isPaid ? grandTotal : keepPartial ? editing.paid_amount : 0,
        payment_mode: isPaid ? paymentMode : 'pending',
        items: validItems,
      };
      const result = editing
        ? await api.post(`/purchases/${editing.id}/update`, payload)
        : await api.post('/purchases', payload);
      const doneMessage = editing ? result.message : 'Purchase recorded successfully. Stock updated.';

      resetForm();

      await loadPurchases();
      await loadMaterials();

      alert(doneMessage);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  /* =====================================================
     PDF INVOICE
     ===================================================== */

  const handlePdfUpload = async (event) => {
    const file =
      event.target.files[0];

    if (!file) return;

    setParsing(true);
    setParsedNotice(null);

    try {
      const result =
        await uploadFile(
          '/invoice-parser/parse-invoice',
          file
        );

      let resolvedSupplierId =
        result.matched_supplier_id;

      if (
        !resolvedSupplierId &&
        result.supplier_name
      ) {
        const newSup =
          await api.post(
            '/purchases/suppliers',
            {
              name:
                result.supplier_name,
            }
          );

        resolvedSupplierId =
          newSup.id;

        loadSuppliers();
      }

      if (resolvedSupplierId) {
        setSupplierId(
          String(resolvedSupplierId)
        );
      }

      if (result.invoice_no) {
        setInvoiceNo(
          result.invoice_no
        );
      }

      if (result.invoice_date) {
        setInvoiceDate(
          result.invoice_date
        );
      }

      let aliasRows = [];
      try { aliasRows = await api.get('/purchases/item-aliases'); } catch (e) { aliasRows = []; }
      const normName = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
      const aliasFor = (desc) => {
        const nm = normName(desc);
        const hit = aliasRows.find((a) => a.invoice_name_norm === nm && String(a.supplier_id) === String(resolvedSupplierId))
          || aliasRows.find((a) => a.invoice_name_norm === nm);
        return hit ? hit.raw_material_id : null;
      };

      const resolvedItems = [];
      let createdCount = 0;

      for (const item of result.items) {
        let materialId =
          aliasFor(item.description) || item.matched_raw_material_id;
        if (materialId && !materials.some((mt) => String(mt.id) === String(materialId))) materialId = null;

        if (!materialId) {
          createdCount++;
        }

        resolvedItems.push({
          raw_material_id:
            materialId ? String(materialId) : '',
          invoice_name:
            item.description || '',
          quantity:
            String(item.quantity),
          rate:
            String(item.rate),
          tax_percent:
            String(
              item.gst_percent || 0
            ),
        });
      }

      setItems(
        resolvedItems.length > 0
          ? resolvedItems
          : [{ ...emptyItem }]
      );

      loadMaterials();

      setParsedNotice({
        itemCount:
          result.items.length,
        createdCount,
        grandTotal:
          result.grand_total,
        supplierName:
          result.supplier_name,
      });
    } catch (err) {
      alert(
        'Could not parse this PDF: ' +
          err.message
      );
    } finally {
      setParsing(false);
      event.target.value = '';
    }
  };

  /* =====================================================
     PURCHASE HISTORY
     ===================================================== */

  const filteredPurchases =
    useMemo(() => {
      const term =
        search.trim().toLowerCase();

      if (!term) {
        return purchases;
      }

      return purchases.filter(
        (purchase) =>
          String(
            purchase.supplier_name ||
              ''
          )
            .toLowerCase()
            .includes(term) ||
          String(
            purchase.invoice_no || ''
          )
            .toLowerCase()
            .includes(term)
      );
    }, [purchases, search]);

  const totalPurchaseValue =
    purchases.reduce(
      (sum, purchase) =>
        sum +
        Number(
          purchase.total_amount || 0
        ),
      0
    );

  const totalPaid =
    purchases.reduce(
      (sum, purchase) =>
        sum +
        Number(
          purchase.paid_amount || 0
        ),
      0
    );

  const totalOutstanding =
    totalPurchaseValue -
    totalPaid;

  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <div className="purchases-page">

      {/* =================================================
          HEADER
          ================================================= */}

      <div className="purchases-header">

        <div>
          <div className="purchases-breadcrumb">
            OPERATIONS / PURCHASES
          </div>

          <h1>Purchases</h1>

          <p>
            Record stock purchases, supplier
            invoices and payments
          </p>
        </div>

        <label className="btn purchases-upload-btn">

          {parsing
            ? 'Parsing Invoice...'
            : '📄 Upload Invoice'}

          <input
            type="file"
            accept="application/pdf"
            onChange={
              handlePdfUpload
            }
            disabled={parsing}
            style={{
              display: 'none',
            }}
          />

        </label>

      </div>

      {/* =================================================
          SUMMARY
          ================================================= */}

      <div className="purchases-summary">

        <div className="purchase-stat-card">

          <div className="purchase-stat-icon">
            🧾
          </div>

          <div>
            <span>
              Total Purchases
            </span>

            <strong>
              {purchases.length}
            </strong>
          </div>

        </div>

        <div className="purchase-stat-card">

          <div className="purchase-stat-icon purple">
            ₹
          </div>

          <div>
            <span>
              Purchase Value
            </span>

            <strong>
              ₹
              {totalPurchaseValue.toLocaleString(
                'en-IN',
                {
                  maximumFractionDigits: 2,
                }
              )}
            </strong>
          </div>

        </div>

        <div className="purchase-stat-card">

          <div className="purchase-stat-icon green">
            ✓
          </div>

          <div>
            <span>
              Paid
            </span>

            <strong className="purchase-green">
              ₹
              {totalPaid.toLocaleString(
                'en-IN',
                {
                  maximumFractionDigits: 2,
                }
              )}
            </strong>
          </div>

        </div>

        <div className="purchase-stat-card">

          <div className="purchase-stat-icon orange">
            !
          </div>

          <div>
            <span>
              Outstanding
            </span>

            <strong className="purchase-orange">
              ₹
              {totalOutstanding.toLocaleString(
                'en-IN',
                {
                  maximumFractionDigits: 2,
                }
              )}
            </strong>
          </div>

        </div>

      </div>

      {/* =================================================
          PURCHASE ENTRY
          ================================================= */}

      <div className="purchase-form-card">
        {editing && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 14px', padding: '10px 14px', borderRadius: 12, background: '#fff3e2', border: '1px solid #f4d7a8', color: '#8a5a00', fontWeight: 700, fontSize: 14 }}>
            <span>Editing purchase #{editing.id}{editing.invoice_no ? ` (${editing.invoice_no})` : ''}. Saving replaces the saved purchase.</span>
            <button type="button" onClick={resetForm} style={{ height: 34, padding: '0 14px', borderRadius: 10, border: '1px solid #e8b96a', background: '#fff', color: '#8a5a00', fontWeight: 800, cursor: 'pointer' }}>Cancel edit</button>
          </div>
        )}

        <div className="purchase-form-header">

          <div>

            <div className="purchase-form-eyebrow">
              {editing ? `EDITING PURCHASE #${editing.id}` : 'NEW PURCHASE'}
            </div>

            <h2>
              Purchase Entry
            </h2>

            <p>
              Add supplier invoice details
              and purchase items
            </p>

          </div>

          <div className="purchase-form-total">

            <span>
              Current Total
            </span>

            <strong>
              ₹
              {grandTotal.toFixed(2)}
            </strong>

          </div>

        </div>

        {/* PARSED NOTICE */}

        {parsedNotice && (
          <div className="purchase-parsed-notice">

            <div className="parsed-icon">
              ✓
            </div>

            <div>

              <strong>
                Invoice imported successfully
              </strong>

              <span>
                Parsed{' '}
                {parsedNotice.itemCount}{' '}
                item(s)
                {parsedNotice.supplierName
                  ? ` from ${parsedNotice.supplierName}`
                  : ''}
                .
                {parsedNotice.createdCount >
                0
                  ? ` ${parsedNotice.createdCount} item(s) need mapping - pick raw materials for red rows.`
                  : ''}
              </span>

            </div>

            <div className="parsed-total">
              {parsedNotice.grandTotal
                ? `₹${Number(
                    parsedNotice.grandTotal
                  ).toFixed(2)}`
                : ''}
            </div>

          </div>
        )}

        <div className="purchase-form-body">

          {/* SUPPLIER SECTION */}

          <div className="purchase-section">

            <div className="purchase-section-heading">

              <div className="purchase-section-icon">
                🏢
              </div>

              <div>
                <h3>
                  Supplier & Invoice
                </h3>

                <span>
                  Basic purchase document details
                </span>
              </div>

            </div>

            <div className="purchase-header-grid">

              <div className="purchase-field supplier-field-large">

                <label>
                  Supplier *
                </label>

                <select
                  value={supplierId}
                  onChange={(e) =>
                    setSupplierId(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    Select supplier
                  </option>

                  {suppliers.map(
                    (supplier) => (
                      <option
                        key={supplier.id}
                        value={supplier.id}
                      >
                        {supplier.name}
                      </option>
                    )
                  )}

                </select>

              </div>

              <div className="purchase-field">

                <label>
                  Invoice Date *
                </label>

                <input
                  type="date"
                  value={
                    invoiceDate
                  }
                  onChange={(e) =>
                    setInvoiceDate(
                      e.target.value
                    )
                  }
                />

              </div>

              <div className="purchase-field">

                <label>
                  Invoice Number
                </label>

                <input
                  placeholder="e.g. INV-1025"
                  value={invoiceNo}
                  onChange={(e) =>
                    setInvoiceNo(
                      e.target.value
                    )
                  }
                />

              </div>

            </div>

            <div className="quick-supplier">

              <span>
                Supplier not listed?
              </span>

              <input
                placeholder="Type supplier name"
                value={newSupplier}
                onChange={(e) =>
                  setNewSupplier(
                    e.target.value
                  )
                }
              />

              <button
                className="btn btn-secondary"
                onClick={addSupplier}
              >
                + Add Supplier
              </button>

            </div>

          </div>

          {/* ITEMS SECTION */}

          <div className="purchase-section">

            <div className="purchase-section-heading">

              <div className="purchase-section-icon">
                📦
              </div>

              <div>
                <h3>
                  Purchase Items
                </h3>

                <span>
                  Add raw materials received
                  in this invoice
                </span>
              </div>

            </div>

            <div className="purchase-items-wrapper">

              <table className="purchase-items-table">

                <thead>
                  <tr>
                    <th>Bill Item</th>
                    <th>
                      Raw Material *
                    </th>

                    <th>
                      Qty *
                    </th>

                    <th>
                      Unit
                    </th>

                    <th>
                      Rate
                    </th>

                    <th>
                      GST %
                    </th>

                    <th>
                      Amount
                    </th>

                    <th />
                  </tr>
                </thead>

                <tbody>

                  {items.map(
                    (item, index) => (
                      <tr key={index}>

                        <td style={{ maxWidth: 220, fontSize: 13, fontWeight: 600, color: item.invoice_name && !item.raw_material_id ? '#e53935' : '#555' }}>
                          {item.invoice_name || '—'}
                        </td>

                        <td>
                          <MaterialPicker
                            materials={materials}
                            value={item.raw_material_id}
                            invalid={!item.raw_material_id && !!item.invoice_name}
                            onChange={(id) => updateItem(index, 'raw_material_id', id)}
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            placeholder="0"
                            value={
                              item.quantity
                            }
                            onChange={(e) =>
                              updateItem(
                                index,
                                'quantity',
                                e.target.value
                              )
                            }
                          />
                        </td>

                        <td>
                          <span className="purchase-unit">
                            {packOf(item.raw_material_id) > 1
                              ? `1 pack = ${packOf(item.raw_material_id)} ${materialUnit(item.raw_material_id)}`
                              : (materialUnit(item.raw_material_id) || '—')}
                            {packOf(item.raw_material_id) > 1 && Number(item.quantity) > 0 && (
                              <small style={{ display: 'block', color: '#776e86', fontWeight: 600 }}>
                                total {(Number(item.quantity) * packOf(item.raw_material_id)).toLocaleString('en-IN')} {materialUnit(item.raw_material_id)}
                              </small>
                            )}
                            {item.raw_material_id && (
                              <button type="button" onClick={() => setPackSize(item.raw_material_id)}
                                style={{ display: 'block', border: 'none', background: 'none', padding: '2px 0 0', color: '#5b3fd9', fontWeight: 800, fontSize: 12, cursor: 'pointer', font: 'inherit' }}>
                                ✎ Pack size
                              </button>
                            )}
                          </span>

                        </td>

                        <td>
                          <div className="purchase-rate-input">
                            <span>
                              ₹
                            </span>

                            <input
                              type="number"
                              min="0"
                              step="any"
                              placeholder="0"
                              value={
                                item.rate
                              }
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  'rate',
                                  e.target.value
                                )
                              }
                            />
                          </div>
                        </td>

                        <td>
                          <div className="purchase-tax-input">

                            <input
                              type="number"
                              min="0"
                              step="any"
                              placeholder="0"
                              value={
                                item.tax_percent
                              }
                              onChange={(e) =>
                                updateItem(
                                  index,
                                  'tax_percent',
                                  e.target.value
                                )
                              }
                            />

                            <span>
                              %
                            </span>

                          </div>
                        </td>

                        <td>
                          <strong className="purchase-line-total">
                            ₹
                            {lineAmount(
                              item
                            ).toFixed(2)}
                          </strong>
                        </td>

                        <td>
                          <button
                            className="purchase-remove-btn"
                            onClick={() =>
                              removeItemLine(
                                index
                              )
                            }
                          >
                            ×
                          </button>
                        </td>

                      </tr>
                    )
                  )}

                </tbody>

              </table>

            </div>

            <button
              className="purchase-add-item-btn"
              onClick={addItemLine}
            >
              + Add Item
            </button>

          </div>

          {/* TOTALS + PAYMENT */}

          <div className="purchase-bottom">

            <div className="purchase-payment-card">

              <div className="purchase-section-heading">

                <div className="purchase-section-icon">
                  💳
                </div>

                <div>
                  <h3>
                    Payment
                  </h3>

                  <span>
                    Record supplier payment
                  </span>
                </div>

              </div>

              <div className="payment-choice">

                <button
                  className={
                    !isPaid
                      ? 'payment-option active'
                      : 'payment-option'
                  }
                  onClick={() =>
                    setIsPaid(false)
                  }
                >
                  <span>
                    ○
                  </span>

                  <div>
                    <strong>
                      Unpaid
                    </strong>

                    <small>
                      Add to outstanding
                    </small>
                  </div>

                </button>

                <button
                  className={
                    isPaid
                      ? 'payment-option active'
                      : 'payment-option'
                  }
                  onClick={() =>
                    setIsPaid(true)
                  }
                >
                  <span>
                    ✓
                  </span>

                  <div>
                    <strong>
                      Paid
                    </strong>

                    <small>
                      Payment completed
                    </small>
                  </div>

                </button>

              </div>

              {isPaid && (
                <div className="payment-mode-field">

                  <label>
                    Payment Method
                  </label>

                  <select
                    value={
                      paymentMode
                    }
                    onChange={(e) =>
                      setPaymentMode(
                        e.target.value
                      )
                    }
                  >
                    <option value="cash">
                      Cash
                    </option>

                    <option value="upi">
                      UPI
                    </option>

                    <option value="bank">
                      Bank
                    </option>
                  </select>

                </div>
              )}

            </div>

            <div className="purchase-totals-card">

              <div className="purchase-total-row">
                <span>
                  Sub Total
                </span>

                <strong>
                  ₹
                  {subTotal.toFixed(2)}
                </strong>
              </div>

              <div className="purchase-total-row tax">
                <span>
                  Total GST
                </span>

                <strong>
                  ₹
                  {totalTax.toFixed(2)}
                </strong>
              </div>

              <div className="purchase-grand-total">

                <span>
                  Grand Total
                </span>

                <strong>
                  ₹
                  {grandTotal.toFixed(2)}
                </strong>

              </div>

              <div className="purchase-balance">

                <span>
                  {isPaid
                    ? 'Payment Status'
                    : 'Amount Outstanding'}
                </span>

                <strong
                  className={
                    isPaid
                      ? 'paid-text'
                      : 'outstanding-text'
                  }
                >
                  {isPaid
                    ? 'PAID'
                    : `₹${grandTotal.toFixed(
                        2
                      )}`}
                </strong>

              </div>

              <button
                className="btn purchase-save-btn"
                onClick={
                  savePurchase
                }
                disabled={saving}
              >
                {saving
                  ? 'Saving Purchase...'
                  : editing ? 'Update Purchase' : 'Save Purchase'}
              </button>

            </div>

          </div>

        </div>

      </div>

      {/* =================================================
          RECENT PURCHASES
          ================================================= */}

      <div className="recent-purchases-card">

        <div className="recent-purchases-header">

          <div>

            <div className="recent-eyebrow">
              HISTORY
            </div>

            <h2>
              Recent Purchases
            </h2>

            <p>
              Review previous supplier invoices
              and payments
            </p>

          </div>

          <div className="purchase-history-search">

            <span>
              ⌕
            </span>

            <input
              placeholder="Search supplier or invoice..."
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
            />

          </div>

        </div>

        {filteredPurchases.length >
        0 ? (
          <div className="recent-purchases-table-wrap">

            <table className="recent-purchases-table">

              <thead>
                <tr>
                  <th>
                    Date
                  </th>

                  <th>
                    Supplier
                  </th>

                  <th>
                    Invoice
                  </th>

                  <th>
                    Total
                  </th>

                  <th>
                    Paid
                  </th>

                  <th>
                    Balance
                  </th>

                  <th>
                    Invoice
                  </th>
                </tr>
              </thead>

              <tbody>

                {filteredPurchases.map(
                  (purchase) => {

                    const total =
                      Number(
                        purchase.total_amount ||
                          0
                      );

                    const paid =
                      Number(
                        purchase.paid_amount ||
                          0
                      );

                    const balance =
                      total - paid;

                    return (
                      <tr
                        key={
                          purchase.id
                        }
                      >

                        <td>
                          <strong>
                            {
                              purchase.purchase_date
                            }
                          </strong>
                        </td>

                        <td>
                          <span className="history-supplier">
                            {
                              purchase.supplier_name
                            }
                          </span>
                        </td>

                        <td>
                          <span className="history-invoice">
                            {
                              purchase.invoice_no ||
                              '—'
                            }
                          </span>
                        </td>

                        <td>
                          <strong>
                            ₹
                            {total.toFixed(
                              2
                            )}
                          </strong>
                        </td>

                        <td>
                          <span className="paid-amount">
                            ₹
                            {paid.toFixed(
                              2
                            )}
                          </span>
                        </td>

                        <td>
                          <span
                            className={
                              balance >
                              0
                                ? 'balance-amount'
                                : 'balance-paid'
                            }
                          >
                            {balance >
                            0
                              ? `₹${balance.toFixed(
                                  2
                                )}`
                              : 'Paid'}
                          </span>
                        </td>

                        <td>
                          <a
                            className="invoice-link"
                            href={`/api/purchases/${purchase.id}/invoice?token=${localStorage.getItem('icecream_token')}&device_token=${localStorage.getItem('icecream_device') || ''}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                          <button
                            type="button"
                            onClick={() => startEdit(purchase)}
                            style={{ marginLeft: 12, border: 'none', background: 'none', padding: 0, color: '#b86a00', fontWeight: 800, cursor: 'pointer', font: 'inherit' }}
                          >
                            Edit
                          </button>
                        </td>

                      </tr>
                    );
                  }
                )}

              </tbody>

            </table>

          </div>
        ) : (
          <div className="purchases-empty">

            <div className="purchases-empty-icon">
              🧾
            </div>

            <strong>
              {purchases.length ===
              0
                ? 'No purchases yet'
                : 'No purchases found'}
            </strong>

            <span>
              {purchases.length ===
              0
                ? 'Your purchase history will appear here.'
                : 'Try a different search.'}
            </span>

          </div>
        )}

      </div>

    </div>
  );
}